import * as React from 'react';
import {
  Input as ChakraInput,
  type InputProps as ChakraInputProps,
} from '@chakra-ui/react';

/**
 * Project Input.
 *
 * Pass-through over Chakra v3's `Input`. NB-style RN props
 * (`onChangeText`, `isReadOnly`) are not supported — migrate them to the
 * HTML standards (`onChange(e)`, `readOnly`) at the call site.
 *
 * Design system field: a faint Evergreen wash with no border, 48px tall and
 * an 8px radius (prototype `.inp`; the Figma frame draws it at 40px, and the
 * prototype is the sizing authority). This replaces the NativeBase baseStyle
 * (48px, pill radius, steel border on white). All values stay overridable.
 *
 * The NB `marginBottom="4"` default is deliberately gone: the design spaces
 * label and field with a parent `gap`, and the implicit margin stacked on top
 * of it produced a doubled gap.
 */

export type InputProps = ChakraInputProps;

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      height = '48px',
      width = '100%',
      maxWidth = '340px',
      color = 'secondary.500',
      bg = 'rgba(4, 51, 56, 0.05)',
      borderWidth = '0',
      rounded = 'option',
      px = '14px',
      fontSize = 'sm',
      ...rest
    },
    ref
  ) => (
    <ChakraInput
      ref={ref}
      height={height}
      width={width}
      maxWidth={maxWidth}
      color={color}
      bg={bg}
      borderWidth={borderWidth}
      rounded={rounded}
      px={px}
      fontSize={fontSize}
      // Figma draws placeholder text as Seafoam at 40%.
      _placeholder={{ color: 'secondary.200', opacity: 0.4 }}
      {...rest}
    />
  )
);
Input.displayName = 'Input';
